import { FC } from 'react';

import ButtonBase from '@material-ui/core/ButtonBase';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import type { ExerciseAnswer } from 'api/rest/exercises/exercises.types';

import { ON_GO_COLORS } from '../onGoColors';
import { resolveOnGoChoiceDisplayState } from './onGoChoiceDisplay';
import OnGoChoiceLabel from './OnGoChoiceLabel';

const useStyles = makeStyles(theme => ({
  choices: {
    display: 'grid',
    gap: theme.spacing(1.25),
  },
  choice: {
    width: '100%',
    justifyContent: 'flex-start',
    textAlign: 'left',
    padding: theme.spacing(2, 2.25),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    transition: 'border-color 120ms ease, background-color 120ms ease',
  },
  selectedChoice: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,
  },
  correctChoice: {
    border: `2px solid ${ON_GO_COLORS.correct}`,
    backgroundColor: ON_GO_COLORS.correctSoft,
  },
  incorrectChoice: {
    border: `2px solid ${ON_GO_COLORS.incorrect}`,
    backgroundColor: ON_GO_COLORS.incorrectSoft,
  },
  mutedChoice: {
    opacity: 0.7,
  },
}));

type OnGoMultiSelectChoicesProps = {
  answers: ExerciseAnswer[];
  correctAnswerIdSet: Set<number>;
  disabled?: boolean;
  isAttemptCorrect: boolean | null;
  isResultPhase: boolean;
  onToggle: (answerId: number) => void;
  selectedAnswerIds: number[];
};

const OnGoMultiSelectChoices: FC<OnGoMultiSelectChoicesProps> = ({
  answers,
  correctAnswerIdSet,
  disabled = false,
  isAttemptCorrect,
  isResultPhase,
  onToggle,
  selectedAnswerIds,
}) => {
  const classes = useStyles();
  const selectedAnswerIdSet = new Set(selectedAnswerIds);

  const getChoiceClassName = (answer: ExerciseAnswer) => {
    const displayState = resolveOnGoChoiceDisplayState(
      isResultPhase,
      selectedAnswerIdSet.has(answer.id),
      correctAnswerIdSet.has(answer.id),
      isAttemptCorrect,
    );

    switch (displayState) {
      case 'question-selected':
        return clsx(classes.choice, classes.selectedChoice);
      case 'question-unselected':
        return classes.choice;
      case 'result-correct':
        return clsx(classes.choice, classes.correctChoice);
      case 'result-incorrect':
        return clsx(classes.choice, classes.incorrectChoice);
      case 'result-muted':
        return clsx(classes.choice, classes.mutedChoice);
    }
  };

  return (
    <div className={classes.choices}>
      {answers.map(answer => {
        const isSelected = selectedAnswerIdSet.has(answer.id);
        return (
          <ButtonBase
            key={answer.id}
            aria-pressed={!isResultPhase ? isSelected : undefined}
            className={getChoiceClassName(answer)}
            disabled={disabled || isResultPhase}
            onClick={() => onToggle(answer.id)}
          >
            <OnGoChoiceLabel label={answer.label} />
          </ButtonBase>
        );
      })}
    </div>
  );
};

export default OnGoMultiSelectChoices;
